// VB-019 — Anchored guided tour popover
// Positions a popover next to a `data-tour` target element.
// Falls back to bottom-right corner if the target is not found or off-screen.
import { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Network } from 'lucide-react';
import { cn } from '../utils';
import { useTranslation } from 'react-i18next';

export interface TourStep {
  id: string;
  /** ViewType to navigate to when this step becomes active. null = stay on current page. */
  view: string | null;
  title: string;
  body: string;
  /**
   * data-tour attribute value of the DOM element to anchor to.
   * If omitted or element not found, falls back to bottom-right.
   */
  target?: string;
  /** Preferred placement relative to the target: 'bottom' | 'top' | 'right' | 'left'. Default: 'bottom'. */
  placement?: 'bottom' | 'top' | 'right' | 'left';
  /**
   * Visual variant.
   * 'featured' — centered card with backdrop, for conceptual steps with no anchor target.
   */
  variant?: 'featured';
}

interface PopoverPosition {
  top: number;
  left: number;
  arrowSide: 'top' | 'bottom' | 'left' | 'right' | null;
  arrowOffset: number; // px offset of arrow along the perpendicular axis
}

const POPOVER_W = 300;
const POPOVER_MARGIN = 12; // gap between target and popover
const ARROW_SIZE = 8;
const SCREEN_PADDING = 12;

function computePosition(
  target: DOMRect,
  popoverH: number,
  preferred: 'bottom' | 'top' | 'right' | 'left'
): PopoverPosition {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const spaceBelow = vh - target.bottom;
  const spaceAbove = target.top;
  const spaceRight = vw - target.right;
  const spaceLeft = target.left;

  // Determine actual placement — prefer requested, fallback if not enough room
  let placement = preferred;
  const needed = popoverH + POPOVER_MARGIN + ARROW_SIZE;
  if (placement === 'bottom' && spaceBelow < needed && spaceAbove >= needed) placement = 'top';
  if (placement === 'top' && spaceAbove < needed && spaceBelow >= needed) placement = 'bottom';
  if (
    placement === 'right' &&
    spaceRight < POPOVER_W + POPOVER_MARGIN &&
    spaceLeft >= POPOVER_W + POPOVER_MARGIN
  )
    placement = 'left';
  if (
    placement === 'left' &&
    spaceLeft < POPOVER_W + POPOVER_MARGIN &&
    spaceRight >= POPOVER_W + POPOVER_MARGIN
  )
    placement = 'right';

  let top = 0;
  let left = 0;
  let arrowSide: PopoverPosition['arrowSide'] = null;
  let arrowOffset = 0;

  if (placement === 'bottom') {
    top = target.bottom + POPOVER_MARGIN;
    left = target.left + target.width / 2 - POPOVER_W / 2;
    arrowSide = 'top';
    arrowOffset = POPOVER_W / 2;
  } else if (placement === 'top') {
    top = target.top - popoverH - POPOVER_MARGIN;
    left = target.left + target.width / 2 - POPOVER_W / 2;
    arrowSide = 'bottom';
    arrowOffset = POPOVER_W / 2;
  } else if (placement === 'right') {
    top = target.top + target.height / 2 - popoverH / 2;
    left = target.right + POPOVER_MARGIN;
    arrowSide = 'left';
    arrowOffset = popoverH / 2;
  } else {
    top = target.top + target.height / 2 - popoverH / 2;
    left = target.left - POPOVER_W - POPOVER_MARGIN;
    arrowSide = 'right';
    arrowOffset = popoverH / 2;
  }

  // Arrow offset is along the long axis of the popover — clamp arrow inside popover bounds
  const MIN_ARROW = ARROW_SIZE * 2 + 4;
  const maxOffset =
    placement === 'top' || placement === 'bottom' ? POPOVER_W - MIN_ARROW : popoverH - MIN_ARROW;
  arrowOffset = Math.max(MIN_ARROW, Math.min(arrowOffset, maxOffset));

  // Clamp popover inside viewport — then correct arrowOffset for any shift applied
  const rawLeft = left;
  const rawTop = top;
  left = Math.max(SCREEN_PADDING, Math.min(left, vw - POPOVER_W - SCREEN_PADDING));
  top = Math.max(SCREEN_PADDING, Math.min(top, vh - popoverH - SCREEN_PADDING));

  // If the popover was pushed left/right, the arrow must move right/left by the same amount
  // to keep pointing at the original target center
  if (placement === 'top' || placement === 'bottom') {
    arrowOffset += rawLeft - left;
  } else {
    arrowOffset += rawTop - top;
  }
  arrowOffset = Math.max(MIN_ARROW, Math.min(arrowOffset, maxOffset));

  return { top, left, arrowSide, arrowOffset };
}

interface OnboardingTourProps {
  steps: TourStep[];
  currentIndex: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}

export function OnboardingTour({
  steps,
  currentIndex,
  onNext,
  onPrev,
  onSkip,
}: OnboardingTourProps) {
  const { t } = useTranslation();
  const step = steps[currentIndex];
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<PopoverPosition | null>(null);
  // Track which step index pos was computed for — used to detect stale pos on step change
  const [posStepIndex, setPosStepIndex] = useState<number>(-1);
  // Track whether we found a target (to apply highlight ring)
  const [targetEl, setTargetEl] = useState<Element | null>(null);

  const recompute = useCallback(() => {
    if (!step?.target || !popoverRef.current) {
      setPos(null);
      setPosStepIndex(-1);
      setTargetEl(null);
      return;
    }
    const el = document.querySelector(`[data-tour="${step.target}"]`);
    if (!el) {
      setPos(null);
      setPosStepIndex(-1);
      setTargetEl(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    // Check element is visible and in viewport
    if (
      rect.width === 0 ||
      rect.height === 0 ||
      rect.bottom < 0 ||
      rect.top > window.innerHeight ||
      rect.right < 0 ||
      rect.left > window.innerWidth
    ) {
      setPos(null);
      setPosStepIndex(-1);
      setTargetEl(null);
      return;
    }
    // Try to scroll into view if partially off screen
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const freshRect = el.getBoundingClientRect();
    const popoverH = popoverRef.current.offsetHeight || 200;
    setPos(computePosition(freshRect, popoverH, step.placement ?? 'bottom'));
    setPosStepIndex(currentIndex);
    setTargetEl(el);
  }, [step, currentIndex]);

  // Recompute on step change (after a short delay to allow navigation/render)
  useEffect(() => {
    setPos(null);
    setPosStepIndex(-1);
    setTargetEl(null);
    const t = setTimeout(recompute, 120);
    return () => clearTimeout(t);
  }, [recompute, currentIndex]);

  // Recompute on resize
  useEffect(() => {
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [recompute]);

  // Highlight ring on target
  useEffect(() => {
    if (!targetEl) return;
    (targetEl as HTMLElement).setAttribute('data-tour-active', 'true');
    return () => {
      (targetEl as HTMLElement).removeAttribute('data-tour-active');
    };
  }, [targetEl]);

  if (!step) return null;

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === steps.length - 1;
  const total = steps.length;

  // --- Featured variant: centered card with semi-transparent backdrop ---
  if (step.variant === 'featured') {
    return (
      <>
        {/* Backdrop — dimmer than full modal, so the canvas behind is still visible */}
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-[9998]" />
        <div
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] bg-zinc-900 border border-zinc-700/80 rounded-2xl shadow-2xl w-[480px] max-w-[calc(100vw-32px)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top bar — progress dots + skip */}
          <div className="flex items-center justify-between px-6 pt-6 pb-1">
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <span
                  key={i}
                  className={cn(
                    'h-1.5 rounded-full transition-all duration-300',
                    i === currentIndex
                      ? 'w-4 bg-blue-400'
                      : i < currentIndex
                        ? 'w-1.5 bg-blue-400/40'
                        : 'w-1.5 bg-zinc-700'
                  )}
                />
              ))}
              <span className="text-xs text-zinc-500 ml-2 tabular-nums">
                {currentIndex + 1}/{total}
              </span>
            </div>
            <button
              onClick={onSkip}
              className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors"
              aria-label={t('onboarding.skip_tour')}
            >
              {t('onboarding.skip_tour')} ×
            </button>
          </div>

          {/* Content */}
          <div className="px-6 pt-5 pb-2">
            <div className="w-10 h-10 rounded-xl bg-violet-500/15 text-violet-400 flex items-center justify-center mb-5">
              <Network className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-semibold text-zinc-100 mb-2.5 leading-tight">
              {step.title}
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed">{step.body}</p>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between px-6 pb-6 pt-4">
            <button
              onClick={onPrev}
              disabled={isFirst}
              className={cn(
                'flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors',
                isFirst
                  ? 'text-zinc-700 cursor-not-allowed'
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
              )}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              {t('onboarding.prev_step')}
            </button>
            <button
              onClick={onNext}
              className="flex items-center gap-1 text-xs font-medium px-3.5 py-1.5 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-950 transition-colors"
            >
              {isLast ? t('onboarding.finish_tour') : t('onboarding.next_step')}
              {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </>
    );
  }
  // --- end featured variant ---

  // pos is "ready" only when it was computed for the current step.
  // Even a non-null pos from the previous step is considered stale.
  const posReady = pos !== null && posStepIndex === currentIndex;

  // Intentional fallback: step has no target — show bottom-right corner.
  const fallback = !posReady && !step.target;

  // Build style: anchored when ready, off-screen while awaiting (keeps ref measurable),
  // or fallback bottom-right for intentionally untargeted steps.
  const style: React.CSSProperties = posReady
    ? { position: 'fixed', top: pos!.top, left: pos!.left, width: POPOVER_W }
    : step.target
      ? { position: 'fixed', top: -9999, left: -9999, width: POPOVER_W } // off-screen until computed
      : { position: 'fixed', bottom: 24, right: 24, width: POPOVER_W }; // intentional fallback

  return (
    <div
      ref={popoverRef}
      style={style}
      className="z-[9999] bg-zinc-900 border border-zinc-700/80 rounded-2xl shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Arrow pointer toward target */}
      {pos?.arrowSide && <ArrowPointer side={pos.arrowSide} offset={pos.arrowOffset} />}

      {/* Top bar — progress dots + skip */}
      <div className="flex items-center justify-between px-4 pt-4 pb-1">
        <div className="flex items-center gap-1.5">
          {steps.map((_, i) => (
            <span
              key={i}
              className={cn(
                'h-1.5 rounded-full transition-all duration-300',
                i === currentIndex
                  ? 'w-4 bg-blue-400'
                  : i < currentIndex
                    ? 'w-1.5 bg-blue-400/40'
                    : 'w-1.5 bg-zinc-700'
              )}
            />
          ))}
          <span className="text-xs text-zinc-500 ml-2 tabular-nums">
            {currentIndex + 1}/{total}
          </span>
        </div>
        <button
          onClick={onSkip}
          className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors leading-none"
          aria-label={t('onboarding.skip_tour')}
        >
          {t('onboarding.skip_tour')} ×
        </button>
      </div>

      {/* Content */}
      <div className="px-4 py-3">
        <h3 className="text-sm font-semibold text-zinc-100 mb-1.5 leading-tight">{step.title}</h3>
        <p className="text-xs text-zinc-400 leading-relaxed">{step.body}</p>
        {/* Fallback hint when no target found — show a small direction pill */}
        {fallback && step.target && (
          <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-zinc-800/80 text-zinc-500 text-[10px]">
            <span>↗</span>
            <span>{t('onboarding.look_at_hint')}</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between px-4 pb-4">
        <button
          onClick={onPrev}
          disabled={isFirst}
          className={cn(
            'flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors',
            isFirst
              ? 'text-zinc-700 cursor-not-allowed'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
          )}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t('onboarding.prev_step')}
        </button>
        <button
          onClick={onNext}
          className="flex items-center gap-1 text-xs font-medium px-3.5 py-1.5 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-950 transition-colors"
        >
          {isLast ? t('onboarding.finish_tour') : t('onboarding.next_step')}
          {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

// Small triangular arrow bridging the popover to its target
function ArrowPointer({
  side,
  offset,
}: {
  side: 'top' | 'bottom' | 'left' | 'right';
  offset: number;
}) {
  const base: React.CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
    pointerEvents: 'none',
  };

  // Arrow built with CSS borders trick — points TOWARD the target
  if (side === 'top') {
    // Arrow at top of popover, pointing up toward target
    return (
      <span
        style={{
          ...base,
          top: -ARROW_SIZE,
          left: offset - ARROW_SIZE,
          borderLeft: `${ARROW_SIZE}px solid transparent`,
          borderRight: `${ARROW_SIZE}px solid transparent`,
          borderBottom: `${ARROW_SIZE}px solid #3f3f46`, // zinc-700
        }}
      />
    );
  }
  if (side === 'bottom') {
    return (
      <span
        style={{
          ...base,
          bottom: -ARROW_SIZE,
          left: offset - ARROW_SIZE,
          borderLeft: `${ARROW_SIZE}px solid transparent`,
          borderRight: `${ARROW_SIZE}px solid transparent`,
          borderTop: `${ARROW_SIZE}px solid #3f3f46`,
        }}
      />
    );
  }
  if (side === 'left') {
    return (
      <span
        style={{
          ...base,
          left: -ARROW_SIZE,
          top: offset - ARROW_SIZE,
          borderTop: `${ARROW_SIZE}px solid transparent`,
          borderBottom: `${ARROW_SIZE}px solid transparent`,
          borderRight: `${ARROW_SIZE}px solid #3f3f46`,
        }}
      />
    );
  }
  // right
  return (
    <span
      style={{
        ...base,
        right: -ARROW_SIZE,
        top: offset - ARROW_SIZE,
        borderTop: `${ARROW_SIZE}px solid transparent`,
        borderBottom: `${ARROW_SIZE}px solid transparent`,
        borderLeft: `${ARROW_SIZE}px solid #3f3f46`,
      }}
    />
  );
}
